import React from 'react';
import { Button, Popover } from 'antd';
import dayjs from 'dayjs';

import useDateFormat from '../../hooks/useDateFormat';
import { formatDisplayDateTime, formatSourceDate } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { HISTORY } from '../icons';
import UserRef from '../users/UserRef';
import ItemSourceLine from './ItemSourceLine';

/**
 * The item's audit line: when it was created and, if it has changed since,
 * when it was last updated — one muted line of bare timestamps, ellipsized,
 * no labels — that opens the labeled "who did it" popover on hover
 * (desktop) or tap (touch).
 *
 *   Sep 12, 2026 3:11 PM · Sep 12, 2026 3:40 PM
 *
 * The line says the labels through the popover, never in the text: the
 * first timestamp is always Created, and a second one appears only when the
 * item has changed since. The popover ALWAYS carries both a Created and an
 * Updated row (an unchanged item's Updated is its creation), names the
 * creator (`item.owner`) and the last editor (`item.updatedBy`, absent on
 * older saves and on writes an automation made) as clickable user
 * references, carries the provenance line for an ingested item
 * (`item.source`), and, when the surface has a history door (`onHistory`),
 * a link to it.
 *
 * Nothing in the line or its popover bubbles a click into the surface
 * behind it (a card tile selects on click).
 */

const stop = event => event.stopPropagation();

const parse = raw => {
	if (!raw) return null;
	const day = dayjs(raw);
	return day.isValid() ? day : null;
};

/**
 * The created time; the updated time (the created time when the record has
 * never been saved since, or predates updatedAt); and whether the two
 * differ — which is what decides if the line shows a second timestamp.
 */
export function auditTimes(item) {
	const created = parse(item?.createdAt || item?.created_at);
	const updatedRaw = parse(item?.updatedAt || item?.updated_at);
	const changed = Boolean(created && updatedRaw && !updatedRaw.isSame(created));
	return { created, updated: updatedRaw || created, changed };
}

/**
 * The date the record was made in the system it came from, when an import
 * said so (`source.createdAt`); null otherwise. The footer shows it in place
 * of keepr's own created time (KPR-1235); sort, primary date and KQL still
 * use keepr's createdAt.
 */
export function sourceCreatedAt(item) {
	const raw = item?.source?.createdAt;
	if (!raw) return null;
	return dayjs(raw).isValid() ? raw : null;
}

const AuditRow = ({ label, when, text, userId, missingText, testId }) => (
	<div className="item-audit-line__row" data-testid={testId || `item-audit-${label.toLowerCase()}`}>
		<div className="micro-label item-audit-line__row-label">{label}</div>
		<div className="item-audit-line__row-when">{text || formatDisplayDateTime(when.toISOString())}</div>
		<div className="item-audit-line__row-who">
			{userId
				? <UserRef userId={userId} size={20} />
				: <span className="item-audit-line__unknown">{missingText}</span>}
		</div>
	</div>
);

export default function ItemAuditLine({ item, onHistory, className = '' }) {
	useDateFormat(); // re-render when the date-format preference changes
	const { created, updated, changed } = auditTimes(item);
	if (!created) return null;

	const ownerId = normalizeId(item?.owner);
	// An unchanged item was last "updated" by whoever created it.
	const updatedById = changed ? normalizeId(item?.updatedBy) : ownerId;
	const sourceCreated = sourceCreatedAt(item);
	const createdText = sourceCreated ? formatSourceDate(sourceCreated) : formatDisplayDateTime(created.toISOString());
	const updatedText = changed ? formatDisplayDateTime(updated.toISOString()) : null;

	const content = (
		<div className="item-audit-line__popover" onClick={stop} onKeyDown={stop} role="presentation">
			{sourceCreated ? (
				<AuditRow label="Made in source" text={formatSourceDate(sourceCreated)} testId="item-audit-source-created" />
			) : null}
			<AuditRow
				label={sourceCreated ? 'Added to keepr' : 'Created'}
				testId="item-audit-created"
				when={created}
				userId={ownerId}
				missingText="Unknown"
			/>
			<AuditRow
				label="Updated"
				when={updated}
				userId={updatedById}
				missingText={changed ? 'Unknown (an automation or an older save)' : 'Unknown'}
			/>
			{item?.source || item?.intake ? <ItemSourceLine source={item.source} intake={item.intake} className="item-audit-line__source" /> : null}
			{typeof onHistory === 'function' ? (
				<Button
					type="link"
					size="small"
					icon={<HISTORY />}
					className="item-audit-line__history"
					onClick={() => onHistory()}
				>
					View history
				</Button>
			) : null}
		</div>
	);

	const classes = ['item-audit-line', className].filter(Boolean).join(' ');

	return (
		<Popover
			content={content}
			trigger={['hover', 'click']}
			placement="bottomLeft"
			mouseEnterDelay={0.25}
			overlayClassName="item-audit-line__overlay"
		>
			<span
				className={classes}
				role="button"
				tabIndex={0}
				aria-label="Created and updated details"
				data-testid="item-audit-line"
				onClick={stop}
				onKeyDown={stop}
			>
				<span className="item-audit-line__created" title={sourceCreated ? 'Made in source' : 'Created'}>{createdText}</span>
				{updatedText ? (
					<>
						<span className="item-audit-line__sep" aria-hidden="true"> · </span>
						<span className="item-audit-line__updated" title="Updated">{updatedText}</span>
					</>
				) : null}
			</span>
		</Popover>
	);
}
